import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@roger/ui-components';
import {
  Check,
  ChevronDown,
  ChevronsUpDown,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import {
  assignRogerDocAccountLink,
  clearRogerDocAccountLink,
  createRogerDocAccountLink,
  listRogerDocAccountLinks,
  type RogerDocClinicianAccount,
} from '../../services/accountLinkApi';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import type { OrgMemberResponse } from './types';

// ─── Helpers ────────────────────────────────────────────────────────────────

function accountLabel(account: RogerDocClinicianAccount): string {
  const name = [account.first_name, account.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const base = name ? `${account.username} (${name})` : account.username;
  return account.admin ? `${base} — admin` : base;
}

function compareAssignableAccounts(
  a: RogerDocClinicianAccount,
  b: RogerDocClinicianAccount
): number {
  const labelCompare = accountLabel(a).localeCompare(
    accountLabel(b),
    undefined,
    {
      sensitivity: 'base',
      numeric: true,
    }
  );
  if (labelCompare !== 0) return labelCompare;

  const emailCompare = (a.email ?? '').localeCompare(b.email ?? '', undefined, {
    sensitivity: 'base',
  });
  if (emailCompare !== 0) return emailCompare;

  return a.id.localeCompare(b.id);
}

function accountSearchFields(account: RogerDocClinicianAccount): string[] {
  return [
    account.username,
    account.first_name,
    account.last_name,
    account.email,
    account.clinician_title,
  ].filter((part): part is string => Boolean(part));
}

function accountMatchesSearch(
  account: RogerDocClinicianAccount,
  query: string
): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  return accountSearchFields(account).some(field =>
    field.toLowerCase().includes(normalizedQuery)
  );
}

/** Unlinked accounts for the current agency (API is scoped by agency.system_name). */
function isAssignableAccount(
  account: RogerDocClinicianAccount,
  agencyId: string | undefined
): boolean {
  if (!agencyId) return false;
  return !account.roger_emr;
}

// ─── Component ──────────────────────────────────────────────────────────────

type RogerDocAccountSectionProps = {
  member: OrgMemberResponse;
  /** Called after a successful mutation so the parent can refresh its data. */
  onChanged: () => void;
};

export const RogerDocAccountSection: React.FC<RogerDocAccountSectionProps> =
  React.memo(function RogerDocAccountSection({ member, onChanged }) {
    const { agencyId, showSuccess, showError } = useStore(
      useShallow(state => ({
        agencyId: state.user?.agency?.id,
        showSuccess: state.showSuccess,
        showError: state.showError,
      }))
    );

    const memberEmail = member.email.toLowerCase();

    const [accounts, setAccounts] = useState<RogerDocClinicianAccount[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [busyAccountId, setBusyAccountId] = useState<string | null>(null);
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const [accountPickerOpen, setAccountPickerOpen] = useState(false);
    const [accountSearchQuery, setAccountSearchQuery] = useState('');

    // Collapsed by default to leave more room for the role assignment UI.
    const [expanded, setExpanded] = useState(false);

    // Create-new-account form
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [firstName, setFirstName] = useState(member.first_name ?? '');
    const [lastName, setLastName] = useState(member.last_name ?? '');
    const [clinicianTitle, setClinicianTitle] = useState('');
    const [username, setUsername] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const loadAccounts = useCallback(async () => {
      setIsLoading(true);
      try {
        // Backend scopes this list to the current agency's RogerDoc tenant
        // (agency.system_name) before returning clinician accounts.
        setAccounts(await listRogerDocAccountLinks());
      } catch (err) {
        console.error('Failed to load RogerDoc accounts:', err);
        showError('Unable to load RogerDoc accounts. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }, [showError]);

    useEffect(() => {
      loadAccounts();
      // Reset transient state when switching members.
      setExpanded(false);
      setSelectedAccountId('');
      setAccountPickerOpen(false);
      setAccountSearchQuery('');
      setShowCreateForm(false);
      setFirstName(member.first_name ?? '');
      setLastName(member.last_name ?? '');
      setClinicianTitle('');
      setUsername('');
    }, [loadAccounts, member.first_name, member.last_name, member.email]);

    const linkedAccounts = useMemo(
      () =>
        accounts.filter(
          a =>
            a.roger_emr &&
            a.roger_emr.roger_emr_agency_id === agencyId &&
            a.roger_emr.user_email.toLowerCase() === memberEmail
        ),
      [accounts, agencyId, memberEmail]
    );

    // Accounts with no link yet are safe to assign without stealing one
    // (admin accounts included).
    const assignableAccounts = useMemo(
      () =>
        accounts
          .filter(a => isAssignableAccount(a, agencyId))
          .slice()
          .sort(compareAssignableAccounts),
      [accounts, agencyId]
    );

    const filteredAssignableAccounts = useMemo(
      () =>
        assignableAccounts.filter(a =>
          accountMatchesSearch(a, accountSearchQuery)
        ),
      [assignableAccounts, accountSearchQuery]
    );

    const selectedAccount = useMemo(
      () => assignableAccounts.find(a => a.id === selectedAccountId),
      [assignableAccounts, selectedAccountId]
    );

    const accountPickerLabel = useMemo(() => {
      if (assignableAccounts.length === 0) {
        return 'No unlinked accounts available';
      }
      if (selectedAccount) {
        return accountLabel(selectedAccount);
      }
      return 'Select an account…';
    }, [assignableAccounts.length, selectedAccount]);

    const refreshAfterChange = useCallback(async () => {
      await loadAccounts();
      onChanged();
    }, [loadAccounts, onChanged]);

    const handleAssign = useCallback(async () => {
      if (!selectedAccountId) return;
      setBusyAccountId(selectedAccountId);
      try {
        await assignRogerDocAccountLink(selectedAccountId, member.email);
        showSuccess('RogerDoc account linked');
        setSelectedAccountId('');
        await refreshAfterChange();
      } catch (err) {
        console.error('Failed to link RogerDoc account:', err);
        showError('Failed to link RogerDoc account. Please try again.');
      } finally {
        setBusyAccountId(null);
      }
    }, [
      selectedAccountId,
      member.email,
      showSuccess,
      showError,
      refreshAfterChange,
    ]);

    const handleClear = useCallback(
      async (accountId: string) => {
        setBusyAccountId(accountId);
        try {
          await clearRogerDocAccountLink(accountId);
          showSuccess('RogerDoc account unlinked');
          await refreshAfterChange();
        } catch (err) {
          console.error('Failed to unlink RogerDoc account:', err);
          showError('Failed to unlink RogerDoc account. Please try again.');
        } finally {
          setBusyAccountId(null);
        }
      },
      [showSuccess, showError, refreshAfterChange]
    );

    const handleCreate = useCallback(async () => {
      if (!firstName.trim() || !lastName.trim()) {
        showError('First and last name are required.');
        return;
      }
      setIsCreating(true);
      try {
        const trimmedUsername = username.trim();
        await createRogerDocAccountLink({
          user_email: member.email,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          clinician_title: clinicianTitle.trim(),
          // Omit when blank so RogerDoc derives the username from the name.
          username: trimmedUsername || undefined,
        });
        showSuccess('RogerDoc account created and linked');
        setShowCreateForm(false);
        setClinicianTitle('');
        setUsername('');
        await refreshAfterChange();
      } catch (err) {
        console.error('Failed to create RogerDoc account:', err);
        // Surface an actionable 4xx message (e.g. duplicate-name conflict);
        // fall back to a generic message for 5xx / network errors.
        showError(
          getBusinessErrorMessage(err) ??
            'Failed to create RogerDoc account. Please try again.'
        );
      } finally {
        setIsCreating(false);
      }
    }, [
      firstName,
      lastName,
      clinicianTitle,
      username,
      member.email,
      showSuccess,
      showError,
      refreshAfterChange,
    ]);

    return (
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <Separator className='my-4' />
        <CollapsibleTrigger className='group flex w-full items-center justify-between gap-2 rounded-md py-2 text-left hover:bg-muted/40'>
          <div className='flex min-w-0 items-center gap-2'>
            <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180' />
            <h3 className='text-sm font-semibold'>RogerDoc Account</h3>
            {linkedAccounts.length > 0 && (
              <Badge variant='secondary' className='text-xs'>
                {linkedAccounts.length} linked
              </Badge>
            )}
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            {!expanded && !isLoading && linkedAccounts.length === 0 && (
              <span className='text-xs text-muted-foreground'>Not linked</span>
            )}
            {isLoading && (
              <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
            )}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent className='space-y-3 overflow-hidden pb-1 pt-1'>
          <p className='text-xs text-muted-foreground'>
            Link this user to one or more RogerDoc accounts so visits sync
            correctly. RogerDoc remains the source of truth.
          </p>

          {/* Currently linked accounts */}
          {linkedAccounts.length > 0 ? (
            <div className='space-y-2'>
              {linkedAccounts.map(account => (
                <div
                  key={account.id}
                  className='flex items-center justify-between rounded-md border px-3 py-2'
                >
                  <div className='flex min-w-0 items-center gap-2'>
                    <Badge variant='secondary' className='text-xs'>
                      {account.username}
                    </Badge>
                    {(account.first_name || account.last_name) && (
                      <span className='truncate text-sm text-muted-foreground'>
                        {[account.first_name, account.last_name]
                          .filter(Boolean)
                          .join(' ')}
                      </span>
                    )}
                  </div>
                  <Button
                    variant='ghost'
                    size='sm'
                    disabled={busyAccountId === account.id}
                    onClick={() => handleClear(account.id)}
                  >
                    {busyAccountId === account.id ? (
                      <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    ) : (
                      <Trash2 className='h-3.5 w-3.5' />
                    )}
                    Unlink
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className='text-sm text-muted-foreground'>
              No RogerDoc account linked yet.
            </p>
          )}

          {/* Link an existing account */}
          <div className='flex items-end gap-2'>
            <div className='min-w-0 flex-1'>
              <label className='mb-1 block text-xs font-medium'>
                Link an existing RogerDoc account
              </label>
              <Popover
                open={accountPickerOpen}
                onOpenChange={open => {
                  setAccountPickerOpen(open);
                  if (!open) setAccountSearchQuery('');
                }}
                modal={false}
              >
                <PopoverTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    role='combobox'
                    aria-expanded={accountPickerOpen}
                    aria-label='Link an existing RogerDoc account'
                    disabled={isLoading || assignableAccounts.length === 0}
                    className='w-full justify-between font-normal'
                  >
                    <span
                      className={
                        selectedAccount
                          ? 'truncate text-foreground'
                          : 'truncate text-muted-foreground'
                      }
                    >
                      {accountPickerLabel}
                    </span>
                    <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 text-muted-foreground' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className='w-[var(--radix-popover-trigger-width)] p-0'
                  align='start'
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder='Search accounts…'
                      className='text-xs'
                      value={accountSearchQuery}
                      onValueChange={setAccountSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty className='py-2 text-center text-xs text-muted-foreground'>
                        No matching accounts.
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredAssignableAccounts.map(account => (
                          <CommandItem
                            key={account.id}
                            value={account.id}
                            onSelect={() => {
                              setSelectedAccountId(account.id);
                              setAccountPickerOpen(false);
                              setAccountSearchQuery('');
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 shrink-0 ${
                                selectedAccountId === account.id
                                  ? 'opacity-100'
                                  : 'opacity-0'
                              }`}
                            />
                            <span className='truncate'>
                              {accountLabel(account)}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <Button
              type='button'
              onClick={handleAssign}
              disabled={
                !selectedAccountId || busyAccountId === selectedAccountId
              }
            >
              {busyAccountId === selectedAccountId && (
                <Loader2 className='mr-1 h-4 w-4 animate-spin' />
              )}
              Link
            </Button>
          </div>

          {/* Create a new account */}
          {showCreateForm ? (
            <div className='space-y-2 rounded-md border p-3'>
              <div className='flex items-center justify-between'>
                <h4 className='text-sm font-medium'>New RogerDoc user</h4>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setShowCreateForm(false)}
                  disabled={isCreating}
                >
                  <X className='h-3.5 w-3.5' />
                </Button>
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <div>
                  <label className='mb-1 block text-xs font-medium'>
                    First name
                  </label>
                  <Input
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder='First name'
                  />
                </div>
                <div>
                  <label className='mb-1 block text-xs font-medium'>
                    Last name
                  </label>
                  <Input
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder='Last name'
                  />
                </div>
              </div>
              <div>
                <label className='mb-1 block text-xs font-medium'>
                  Title (optional)
                </label>
                <Input
                  value={clinicianTitle}
                  onChange={e => setClinicianTitle(e.target.value)}
                  placeholder='e.g. RN'
                />
              </div>
              <div>
                <label className='mb-1 block text-xs font-medium'>
                  Username (optional)
                </label>
                <Input
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder='Auto-generated from name if blank'
                />
              </div>
              <div className='flex justify-end'>
                <Button
                  type='button'
                  onClick={handleCreate}
                  disabled={isCreating}
                >
                  {isCreating && (
                    <Loader2 className='mr-1 h-4 w-4 animate-spin' />
                  )}
                  Create &amp; link
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setShowCreateForm(true)}
            >
              <Plus className='h-4 w-4' />
              Create new RogerDoc user
            </Button>
          )}
        </CollapsibleContent>
      </Collapsible>
    );
  });
